import { useState, useEffect, useCallback } from 'react';
import { departmentsAPI, ticketsAPI, usersAPI, categoriesAPI, sitesAPI } from '@/lib/api';
import { TICKET_CATEGORIES } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Plus, UserPlus, Search } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { mstLocalToUtcIso } from '@/lib/timezone';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export default function NewTicketDialog({ open, onOpenChange, onCreated, currentUser }) {
  const [departments, setDepartments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    username: '',
    customer_id: ''
  });
  
  // Check if current user is agent or admin
  const isAgentOrAdmin = currentUser?.role === 'agent' || currentUser?.role === 'admin';
  
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    department_id: '',
    customer_id: isAgentOrAdmin ? '' : (currentUser?.id || ''), // Auto-set for customers
    site_id: '',  // Optional site selection — auto-picked when customer has 1 site
    tags: [],
    category: '',
    // scheduled_at_mst is the user-entered MST-local string ("2026-04-28T14:30").
    // It's converted to a UTC ISO at submit time. We keep it as MST-local in the
    // form so the picker always reads/writes in MST regardless of browser TZ.
    scheduled_at_mst: '',
  });
  const [customerSites, setCustomerSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  // All sites across all customers — drives the "Find by Site" combobox so
  // agents can start the ticket from the site (which auto-fills its customer).
  const [allSites, setAllSites] = useState([]);
  const [siteSearchOpen, setSiteSearchOpen] = useState(false);

  const loadDepartments = useCallback(async () => {
    try {
      const res = await departmentsAPI.list();
      setDepartments(res.data);
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const res = await usersAPI.list({ role: 'customer' });
      // Defensive: backend should already filter to role=customer, but
      // also exclude the currently-logged-in user (e.g. an admin who has
      // also been seeded as a customer should never appear as a target
      // customer in their own ticket form).
      const list = (res.data || []).filter(
        (u) => u.role === 'customer' && u.id !== currentUser?.id
      );
      setCustomers(list);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (open) {
      loadDepartments();
      // Load admin-managed categories (falls back to TICKET_CATEGORIES if API fails)
      categoriesAPI.list({ type: 'ticket' })
        .then((res) => setCategories((res.data || []).map((c) => c.name)))
        .catch(() => setCategories(TICKET_CATEGORIES));
      if (isAgentOrAdmin) {
        loadCustomers(); // Only load customers list for agents/admins
        // Load every site across all customers for the "Find by Site" lookup.
        sitesAPI.list().then((res) => setAllSites(res.data || [])).catch(() => setAllSites([]));
      } else {
        // For customers, auto-set their own ID
        setFormData(prev => ({ ...prev, customer_id: currentUser?.id || '' }));
        setSelectedCustomer(currentUser);
      }
    }
  }, [open, currentUser, isAgentOrAdmin, loadDepartments, loadCustomers]);

  // Whenever the chosen customer changes, fetch their sites and auto-pick when
  // the customer has exactly one site (per spec).
  useEffect(() => {
    const customerId = formData.customer_id;
    if (!open || !customerId) {
      setCustomerSites([]);
      if (formData.site_id) setFormData((p) => ({ ...p, site_id: '' }));
      return;
    }
    let cancelled = false;
    setLoadingSites(true);
    sitesAPI.list({ customer_id: customerId })
      .then((res) => {
        if (cancelled) return;
        const list = res.data || [];
        setCustomerSites(list);
        setFormData((p) => {
          // Validate any pre-selected site still belongs to this customer.
          const stillValid = list.find((s) => s.id === p.site_id);
          if (list.length === 1) {
            return { ...p, site_id: list[0].id };
          }
          if (!stillValid && p.site_id) {
            return { ...p, site_id: '' };
          }
          return p;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load customer sites', err);
        setCustomerSites([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSites(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData.customer_id]);

  const handleCreateNewCustomer = async () => {
    try {
      setLoading(true);
      // Generate a secure random password
      const generateSecurePassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        const length = 16;
        let password = '';
        const array = new Uint8Array(length);
        crypto.getRandomValues(array);
        for (let i = 0; i < length; i++) {
          password += chars[array[i] % chars.length];
        }
        return password;
      };
      
      const res = await usersAPI.create({
        ...newCustomerData,
        password: generateSecurePassword(),
        role: 'customer'
      });
      
      // Add new customer to list
      const newCustomer = res.data;
      setCustomers([...customers, newCustomer]);
      setSelectedCustomer(newCustomer);
      setFormData({ ...formData, customer_id: newCustomer.id });
      
      // Reset form and hide
      setNewCustomerData({
        name: '',
        email: '',
        username: '',
        customer_id: ''
      });
      setShowNewCustomerForm(false);
    } catch (err) {
      console.error('Failed to create customer:', err);
      alert('Failed to create customer. Customer ID or email might already exist.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      alert('Please select a customer');
      return;
    }
    if (customerSites.length > 1 && !formData.site_id) {
      alert('Please select a site for this customer');
      return;
    }
    
    try {
      setLoading(true);
      const payload = {
        ...formData,
        department_id: formData.department_id || undefined,
        category: formData.category || undefined,
        site_id: formData.site_id || undefined,
        // Times are entered in MST. Convert to a UTC ISO string at submit so
        // the backend (and downstream calendar) store a single source of truth.
        scheduled_at: formData.scheduled_at_mst ? mstLocalToUtcIso(formData.scheduled_at_mst) : undefined,
      };
      delete payload.scheduled_at_mst;
      const res = await ticketsAPI.create(payload);
      onOpenChange(false);
      
      // Reset form
      setFormData({
        subject: '',
        description: '',
        priority: 'medium',
        department_id: '',
        customer_id: '',
        site_id: '',
        tags: [],
        category: '',
        scheduled_at_mst: '',
      });
      setSelectedCustomer(null);
      setCustomerSites([]);
      setShowNewCustomerForm(false);
      
      onCreated?.(res.data);
    } catch (err) {
      console.error('Failed to create ticket:', err);
      alert('Failed to create ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] max-h-[90dvh] overflow-y-auto overscroll-contain" data-testid="new-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-800">Create New Ticket</DialogTitle>
          <DialogDescription className="text-slate-600">
            Select a customer and describe the issue
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Quick Site lookup — shortcut for agents who'd rather start from
              the site (it auto-fills the matching customer + site). */}
          {isAgentOrAdmin && !showNewCustomerForm && (
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-xs">Find by Site (optional shortcut)</Label>
              <Popover open={siteSearchOpen} onOpenChange={setSiteSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={siteSearchOpen}
                    className="w-full justify-between font-normal border-slate-300 bg-white"
                    data-testid="site-search-trigger"
                  >
                    <span className="flex items-center gap-2 text-slate-500">
                      <Search className="w-4 h-4" />
                      Search sites by name or address…
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-[60]" align="start">
                  <Command>
                    <CommandInput placeholder="Type a site name or address…" />
                    <CommandList>
                      <CommandEmpty>No sites found.</CommandEmpty>
                      <CommandGroup>
                        {allSites.map((s) => (
                          <CommandItem
                            key={s.id}
                            value={`${s.name} ${s.address || ''} ${s.customer_name || ''}`}
                            onSelect={() => {
                              // Picking a site auto-fills its customer + site.
                              const cust = customers.find((c) => c.id === s.customer_id);
                              if (cust) setSelectedCustomer(cust);
                              setFormData((p) => ({
                                ...p,
                                customer_id: s.customer_id || '',
                                site_id: s.id,
                              }));
                              setSiteSearchOpen(false);
                            }}
                            data-testid={`site-search-option-${s.id}`}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium text-slate-800">{s.name}</span>
                              <span className="text-xs text-slate-500">
                                {s.customer_name || '—'}{s.address ? ` · ${s.address}` : ''}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Customer Selection - ONLY for Agents/Admins */}
          {isAgentOrAdmin && (
            <div className="space-y-2 border-l-4 border-cyan-500 pl-4">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 font-semibold">
                  Customer <span className="text-red-500">*</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewCustomerForm(!showNewCustomerForm)}
                  className="text-cyan-600 hover:text-cyan-700"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  {showNewCustomerForm ? 'Cancel' : 'Add New Customer'}
                </Button>
              </div>

            {showNewCustomerForm ? (
              /* Inline New Customer Form */
              <div className="bg-slate-50 p-4 rounded-lg space-y-3 border border-slate-200">
                <h4 className="font-medium text-slate-800 text-sm">Create New Customer</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Full Name *</Label>
                    <Input
                      value={newCustomerData.name}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                      placeholder="John Doe"
                      className="h-9"
                      required={showNewCustomerForm}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Email *</Label>
                    <Input
                      type="email"
                      value={newCustomerData.email}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="h-9"
                      required={showNewCustomerForm}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Username *</Label>
                    <Input
                      value={newCustomerData.username}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, username: e.target.value })}
                      placeholder="johndoe"
                      className="h-9"
                      required={showNewCustomerForm}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Customer ID *</Label>
                    <Input
                      value={newCustomerData.customer_id}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, customer_id: e.target.value })}
                      placeholder="CUS-00001"
                      className="h-9"
                      required={showNewCustomerForm}
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleCreateNewCustomer}
                  disabled={loading || !newCustomerData.name || !newCustomerData.email || !newCustomerData.username || !newCustomerData.customer_id}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 h-9"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create & Select Customer
                </Button>
              </div>
            ) : (
              /* Customer Search/Select */
              <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between h-11 bg-white border-slate-200"
                    data-testid="customer-select"
                  >
                    {selectedCustomer ? (
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{selectedCustomer.name}</span>
                        <span className="text-xs text-slate-500">({selectedCustomer.email})</span>
                      </span>
                    ) : (
                      <span className="text-slate-500">Search and select customer...</span>
                    )}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[500px] p-0">
                  <Command>
                    <CommandInput placeholder="Search customers by name, email, or ID..." />
                    {/* CommandList provides the proper scroll container — without
                        it, mouse-wheel events don't propagate to CommandGroup. */}
                    <CommandList className="max-h-[300px]">
                      <CommandEmpty>No customer found.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem
                            key={customer.id}
                            value={`${customer.name} ${customer.email} ${customer.customer_id || ''}`}
                            onSelect={() => {
                              setSelectedCustomer(customer);
                              setFormData({ ...formData, customer_id: customer.id });
                              setCustomerSearchOpen(false);
                            }}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center justify-between w-full">
                              <div>
                                <p className="font-medium text-slate-800">{customer.name}</p>
                                <p className="text-xs text-slate-500">{customer.email}</p>
                              </div>
                              {customer.customer_id && (
                                <span className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded">
                                  {customer.customer_id}
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
          )}
          
          {/* Customer info display for customers (read-only) */}
          {!isAgentOrAdmin && currentUser && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Label className="text-xs text-slate-500 mb-2 block">Creating ticket as:</Label>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 font-semibold">
                  {currentUser.name?.charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-slate-800">{currentUser.name}</p>
                  <p className="text-xs text-slate-500">{currentUser.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Site selector — appears directly under the customer name so techs
              see at a glance which site this ticket is for. Auto-picks when the
              customer has exactly one site (per spec). */}
          <div className="space-y-2">
            <Label htmlFor="ticket-site" className="text-slate-700">
              Site{customerSites.length > 1 && <span className="text-red-500"> *</span>}
            </Label>
            {!formData.customer_id ? (
              <Input
                disabled
                value=""
                placeholder="Select a customer first"
                className="bg-slate-50 border-slate-200"
                data-testid="ticket-site-disabled"
              />
            ) : loadingSites ? (
              <div className="flex items-center gap-2 h-10 px-3 text-sm text-slate-500 bg-white border border-slate-200 rounded-md">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading sites...
              </div>
            ) : customerSites.length === 0 ? (
              <Input
                disabled
                value="No sites for this customer"
                className="bg-slate-50 border-slate-200 text-slate-500"
                data-testid="ticket-site-empty"
              />
            ) : customerSites.length === 1 ? (
              <Input
                disabled
                value={`${customerSites[0].name}${customerSites[0].address ? ` — ${customerSites[0].address}` : ''}`}
                className="bg-slate-50 border-slate-200 text-slate-700"
                title="Auto-picked: this customer has only one site"
                data-testid="ticket-site-autopicked"
              />
            ) : (
              <Select
                value={formData.site_id || ''}
                onValueChange={(v) => setFormData({ ...formData, site_id: v })}
              >
                <SelectTrigger id="ticket-site" className="bg-white border-slate-200" data-testid="ticket-site-select">
                  <SelectValue placeholder="Select site..." />
                </SelectTrigger>
                <SelectContent>
                  {customerSites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.address ? ` — ${s.address}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject" className="text-slate-700">Subject <span className="text-red-500">*</span></Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Brief summary of the issue"
              className="bg-white border-slate-200"
              required
              data-testid="ticket-subject-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-slate-700">Description <span className="text-red-500">*</span></Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Provide details about the issue..."
              className="bg-white border-slate-200 min-h-[120px]"
              required
              data-testid="ticket-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Priority</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger className="bg-white border-slate-200" data-testid="ticket-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Department</Label>
              <Select 
                value={formData.department_id} 
                onValueChange={(value) => setFormData({ ...formData, department_id: value })}
              >
                <SelectTrigger className="bg-white border-slate-200" data-testid="ticket-department-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ticket-category" className="text-slate-700">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger id="ticket-category" className="bg-white border-slate-200" data-testid="ticket-category-select">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {(categories.length ? categories : TICKET_CATEGORIES).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ticket-scheduled" className="text-slate-700">
                Schedule (optional) <span className="text-xs text-slate-500 font-normal">— times are MST</span>
              </Label>
              <Input
                id="ticket-scheduled"
                type="datetime-local"
                value={formData.scheduled_at_mst}
                onChange={(e) => setFormData({ ...formData, scheduled_at_mst: e.target.value })}
                className="bg-white border-slate-200"
                data-testid="ticket-scheduled-input"
              />
              <p className="text-[11px] text-slate-500">Will appear on the Shared Calendar at this MST time.</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => {
                onOpenChange(false);
                setShowNewCustomerForm(false);
              }}
              className="text-slate-600 hover:text-slate-800"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-cyan-500 hover:bg-cyan-600 rounded-full"
              disabled={loading || !formData.customer_id}
              data-testid="submit-ticket-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Ticket'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
